// btw-front/src/pages/PartnerDealDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message, Modal, DatePicker } from "antd";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/ko_KR";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/Textarea";
import { CompanySearchSelect } from "../components/ui/CompanySearchSelect";
import { usePartnerSession } from "../context/PartnerSessionContext";
import { goBackOr } from "../lib/nav";
import {
  normalizeCustomerBusinessNumber,
  formatCustomerBusinessNumber,
  isBusinessNumberComplete,
} from "../lib/customerBusinessNumber";
import {
  get as getDealPartner,
  update as updateDealPartner,
  uploadQuotation,
  downloadQuotation as downloadQuotationPartner,
  remove as removeDealPartner,
  adopt as adoptPartner,
  uploadAdoptionFiles as uploadAdoptionFilesPartner,
  removeAdoptionFile as removeAdoptionFilePartner,
  downloadAdoptionFile as downloadAdoptionFilePartner,
} from "../api/partnerDeals";
import {
  get as getDealAdmin,
  update as updateDealAdmin,
  downloadQuotation as downloadQuotationAdmin,
  remove as removeDealAdmin,
  adopt as adoptAdmin,
  uploadAdoptionFiles as uploadAdoptionFilesAdmin,
  removeAdoptionFile as removeAdoptionFileAdmin,
  downloadAdoptionFile as downloadAdoptionFileAdmin,
} from "../api/adminDeals";
import AdoptionFilePicker from "../components/deal/AdoptionFilePicker";
import AdoptionFilesSection from "../components/deal/AdoptionFilesSection";
import DealMessagesSection from "../components/deal/DealMessagesSection";
import DealChangeHistory from "../components/admin/DealChangeHistory";
import DealOwnerChangeModal from "../components/admin/DealOwnerChangeModal";
import { listSalesReps } from "../api/partnerReps";
import { SALES_STAGES } from "../constants/dealStatus";
import { isReviewPending, isReviewRejected } from "../constants/dealReview";
import { DealStageBadge } from "../components/partner/DealStageBadge";
import {
  formatPhone,
  formatAmount,
  parseAmount,
  formatQuarter,
  formatLocalDate,
  formatLocalDateTime,
} from "../lib/format";
import { elapsedDays } from "../lib/elapsedDays";

function formatKRW(n) {
  return new Intl.NumberFormat("ko-KR").format(n || 0);
}

// yyyy-MM-dd → "yyyyQn" — PartnerDealNew 와 동일 패턴
function quarterFromDate(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return "";
  const m = yyyyMmDd.match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (!m) return "";
  const month = Number(m[2]);
  if (month < 1 || month > 12) return "";
  const q = Math.floor((month - 1) / 3) + 1;
  return `${m[1]}Q${q}`;
}

// 수정 가능 필드 — form ↔ deal 매핑에 사용
const EDITABLE_FIELDS = [
  "projectName",
  "customerCompany",
  "customerBusinessNumber",
  "customerContactName",
  "customerContactPosition",
  "customerPhone",
  "customerEmail",
  "partnerSalesRepId",
  "poExpectedDate",
  "productDetail",
  "poAmount",
  "vatExcluded",
  "salesProgress",
  "channel",
  "pocRequired",
  "brochureDelivered",
];

function dealToForm(d) {
  const f = {};
  EDITABLE_FIELDS.forEach((k) => {
    f[k] = d[k] == null ? "" : d[k];
  });
  // 날짜는 yyyy-MM-dd 로 자르기
  if (f.poExpectedDate) f.poExpectedDate = String(f.poExpectedDate).slice(0, 10);
  // boolean 보장
  f.vatExcluded = !!d.vatExcluded;
  f.brochureDelivered = !!d.brochureDelivered;
  // pocRequired 기본값
  if (!["required", "not_required", "undecided"].includes(f.pocRequired))
    f.pocRequired = "undecided";
  return f;
}

export default function PartnerDealDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, profile } = usePartnerSession();

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [adminMemo, setAdminMemo] = useState("");
  const [memoSaving, setMemoSaving] = useState(false);
  // 관리자 담당자 변경 팝업 + 변경 후 이력 재조회용 키(updated_at 은 담당자 변경으로 바뀌지 않는다)
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [quotationUploading, setQuotationUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  // 수정 모드 — 헤더 '수정' 버튼 클릭 시 진입. form 은 deal 스냅샷.
  const [editMode, setEditMode] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 목록으로 복귀 — 온 곳(대시보드의 해당 탭 등)으로 되돌아감. 없으면 역할별 기본 목록.
  const backToList = () => goBackOr(navigate, role === "admin" ? "/admin" : "/partners/dashboard");

  // 삭제 — Modal.confirm 으로 사용자 동의 받은 후 소프트 삭제 호출.
  // 성공 시 본인 회사 목록(파트너) 또는 어드민 목록으로 이동.
  function handleDelete() {
    // 삭제 사유(선택) — 다이얼로그 textarea 입력을 클로저 변수로 수집
    let reason = "";
    Modal.confirm({
      title: t("partners.deals.detail.deleteConfirmTitle"),
      content: (
        <div>
          <p>{t("partners.deals.detail.deleteConfirmContent")}</p>
          <textarea
            className="mt-3 w-full rounded-lg border border-slate-300 p-2 text-sm"
            rows={3}
            maxLength={500}
            placeholder={t("partners.deals.detail.deleteReasonPlaceholder")}
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: t("partners.deals.detail.deleteConfirmOk"),
      cancelText: t("partners.deals.detail.deleteConfirmCancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        if (deleting) return;
        setDeleting(true);
        try {
          const fn = role === "admin" ? removeDealAdmin : removeDealPartner;
          await fn(id, reason.trim());
          message.success(t("partners.deals.detail.deleteDone"));
          backToList();
        } catch (err) {
          const msg =
            (err && err.payload && err.payload.error && err.payload.error.message) ||
            t("partners.deals.detail.deleteFailed");
          message.error(msg);
        } finally {
          setDeleting(false);
        }
      },
    });
  }
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // 영업단계 변경 다이얼로그 — partner 가 메모를 입력해 관리자에게 전달
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [stageDialogTarget, setStageDialogTarget] = useState(null);
  const [stageDialogMemo, setStageDialogMemo] = useState("");
  // '도입' 전환 다이얼로그에서 선택한 증빙 파일(File[]) — 업로드는 확인 시 1회
  const [stageFiles, setStageFiles] = useState([]);
  const [stageSubmitting, setStageSubmitting] = useState(false);
  // 영업대표 셀렉트 옵션 — editMode 진입 시 1회 로딩
  const [salesReps, setSalesReps] = useState([]);
  const selectedRep = salesReps.find((r) => String(r.id) === String(form.partnerSalesRepId));

  function setField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function enterEdit() {
    if (!deal) return;
    setForm(dealToForm(deal));
    setEditMode(true);
    // partner 만 셀렉트 사용 — admin 은 영업대표 변경하지 않음
    if (isPartner) {
      listSalesReps()
        .then((rows) => setSalesReps(rows))
        .catch(() => setSalesReps([]));
    }
  }

  function cancelEdit() {
    setEditMode(false);
    setForm({});
  }

  async function saveEdit() {
    if (saving) return;
    if (!form.projectName || !form.customerCompany || !form.customerContactName) {
      message.warning(t("partners.signup.errors.required"));
      return;
    }
    if (isPartner && !form.partnerSalesRepId) {
      message.warning(t("partners.signup.errors.required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        projectName: form.projectName,
        customerCompany: form.customerCompany,
        customerBusinessNumber:
          normalizeCustomerBusinessNumber(form.customerBusinessNumber) || null,
        customerContactName: form.customerContactName,
        customerContactPosition: form.customerContactPosition || null,
        customerPhone: form.customerPhone || null,
        customerEmail: form.customerEmail || null,
        poExpectedDate: form.poExpectedDate || null,
        poExpectedQuarter: quarterFromDate(form.poExpectedDate) || null,
        productDetail: form.productDetail || null,
        poAmount: Math.max(0, Number(form.poAmount) || 0),
        vatExcluded: !!form.vatExcluded,
        salesProgress: form.salesProgress || null,
        channel: form.channel || null,
        pocRequired: form.pocRequired || "undecided",
        brochureDelivered: !!form.brochureDelivered,
      };
      // 영업대표 — partner 만 변경 가능. 서버가 스냅샷 복사(name/phone/email).
      if (isPartner) payload.partnerSalesRepId = form.partnerSalesRepId;
      const fn = isAdmin ? updateDealAdmin : updateDealPartner;
      await fn(id, payload);
      // 화면 표시값 갱신 — 영업대표 변경 시 셀렉트에서 찾은 스냅샷으로 즉시 반영
      const localPatch = { ...payload };
      if (isPartner && selectedRep) {
        localPatch.salesRepName = selectedRep.name;
        localPatch.salesRepPhone = selectedRep.phone;
        localPatch.salesRepEmail = selectedRep.email;
      }
      setDeal((d) => ({ ...d, ...localPatch }));
      setEditMode(false);
      message.success(t("partners.deals.detail.editSaved"));
      // 최종 수정일(updatedAt)은 서버 값이라 재조회로 맞춘다. 실패 시 로컬 반영 유지(저장은 성공)
      try {
        await reloadDeal();
      } catch (e) {
        // 무시
      }
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("partners.deals.detail.editSaveFailed");
      message.error(msg);
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    // admin 은 partner_id 검증 없는 별도 endpoint 사용 — 모든 딜 접근 가능
    const fetcher = role === "admin" ? getDealAdmin : getDealPartner;
    fetcher(id)
      .then((d) => {
        if (cancelled) return;
        setDeal(d);
        setAdminMemo(d.adminMemo || "");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err && err.status === 404) setNotFound(true);
        else setNotFound(true); // 권한 없음(403)도 같은 화면
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, role]);

  const isAdmin = role === "admin";
  const isPartner = role === "partner";
  // 관리자는 본인 등록 딜만 수정 가능 — 서버 PUT 가드(본인 외 403)와 UI 를 일치.
  // 파트너/타 관리자 딜에서는 영업단계·수정 버튼을 숨긴다(항상 403 이던 죽은 버튼 제거).
  // 열람·내부 메모·삭제는 기존대로 관리자 전체 허용.
  const isOwnAdminDeal =
    isAdmin &&
    !!deal &&
    deal.isAdminCreated &&
    String(deal.createdByAdminId) === String(profile && profile.userId);
  // 파트너: 같은 회사 딜은 모두 보이지만 수정·삭제·견적서·영업단계 변경은 **담당 계정만**.
  // 서버가 계산해 내려준 canManage 를 우선 쓴다(routes/partner/dealOwnership.js 와 같은 판정).
  // 폴백도 담당 계정(관리자 담당자 변경 후 등록 계정과 다를 수 있음) → 없으면 등록 계정.
  const isOwnPartnerDeal =
    isPartner &&
    !!deal &&
    (typeof deal.canManage === "boolean"
      ? deal.canManage
      : String(deal.ownerUserId || deal.createdByUserId) === String(profile && profile.userId));
  // 삭제된 딜 — 관리자 열람 전용(모든 변경 액션 차단, 서버도 is_deleted=0 필터로 차단)
  const isDeletedDeal = !!(deal && deal.isDeleted);
  // 심사 상태 — 승인 전에는 '도입 전환' 을 막는다(서버도 409 DEAL_NOT_APPROVED 로 차단).
  // 인정되지도 않은 딜이 '도입 완료' 로 올라가 실적에 잡히는 것을 막는 규칙이라 화면도 맞춘다.
  const reviewPending = isReviewPending(deal);
  const reviewRejected = isReviewRejected(deal);
  const notApproved = reviewPending || reviewRejected;
  // 견적서 업로드/교체는 수정 모드에서만 (read 모드는 다운로드 전용)
  const canUploadQuotation = isOwnPartnerDeal && editMode;
  // 승인 전에는 영업단계 변경을 감춘다 — 어떤 파이프라인에도 들어가 있지 않아 의미가 없고,
  // '도입' 버튼이 보이면 눌렀다가 서버 409(DEAL_NOT_APPROVED)를 맞는다.
  const canChangeStatus = (isOwnPartnerDeal || isOwnAdminDeal) && !isDeletedDeal && !notApproved;
  // 도입 증빙 파일 교체(추가·삭제) — 도입 처리한 본인만 (서버 403 가드와 일치).
  // 파트너는 adoptedByUserId, 관리자는 adoptedByAdminId 와 세션 userId 비교.
  const adoptionFiles = (deal && deal.adoptionFiles) || [];
  const canManageAdoptionFiles =
    !!deal &&
    !isDeletedDeal &&
    ((isPartner &&
      !!deal.adoptedByUserId &&
      String(deal.adoptedByUserId) === String(profile && profile.userId)) ||
      (isAdmin &&
        !!deal.adoptedByAdminId &&
        String(deal.adoptedByAdminId) === String(profile && profile.userId)));
  // 섹션 노출: 도입 상태이거나 증빙 파일이 남아 있는 딜(단계 되돌림 후에도 이력 표시)
  const showAdoptionSection = !!deal && (deal.status === "adoption" || adoptionFiles.length > 0);
  const isAdoptionTarget = stageDialogTarget === "adoption";
  // 도입 전환 확인 가능 조건: 새 파일 ≥1 또는 기존 증빙 파일 존재(재도입)
  const adoptionOkDisabled =
    isAdoptionTarget && stageFiles.length === 0 && adoptionFiles.length === 0;

  // 서버 최신 상태 재조회 — 도입 전환·파일 교체 후 처리자/파일 목록을 서버 기준으로 동기화
  async function reloadDeal() {
    const fetcher = isAdmin ? getDealAdmin : getDealPartner;
    const d = await fetcher(id);
    setDeal(d);
    return d;
  }

  // 영업단계 변경 — 다이얼로그 열기 (partner 는 메모 입력, admin 은 단순 confirm).
  // '도입' 은 증빙 파일 피커가 추가로 열린다(최소 1개 필수).
  function handleStatusChange(nextStatus) {
    if (!deal || deal.status === nextStatus) return;
    setStageDialogTarget(nextStatus);
    setStageDialogMemo("");
    setStageFiles([]);
    setStageDialogOpen(true);
  }

  // '도입' 전환 — 전용 multipart 엔드포인트(status+파일 한 트랜잭션). 낙관적 업데이트 없이 응답 후 재조회.
  async function confirmAdoption() {
    if (stageSubmitting) return;
    setStageSubmitting(true);
    try {
      const memo = stageDialogMemo.trim();
      const fn = isAdmin ? adoptAdmin : adoptPartner;
      await fn(id, { files: stageFiles, partnerMemo: isPartner ? memo : "" });
      setStageDialogOpen(false);
      setStageFiles([]);
      message.success(t("partners.deals.detail.statusChanged"));
      try {
        await reloadDeal();
      } catch (e) {
        // 재조회 실패 시 로컬 반영(전환 자체는 성공)
        setDeal((d) => ({ ...d, status: "adoption", partnerMemo: memo || d.partnerMemo }));
      }
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        (err && err.message) ||
        t("partners.deals.detail.statusChangeFailed");
      message.error(msg);
    } finally {
      setStageSubmitting(false);
    }
  }

  // 증빙 파일 추가/삭제/다운로드 — AdoptionFilesSection 콜백. 응답의 최신 목록으로 동기화.
  async function handleAdoptionAdd(files) {
    const fn = isAdmin ? uploadAdoptionFilesAdmin : uploadAdoptionFilesPartner;
    const res = await fn(id, files);
    setDeal((d) => ({ ...d, adoptionFiles: (res && res.adoptionFiles) || d.adoptionFiles }));
  }
  async function handleAdoptionRemove(file) {
    const fn = isAdmin ? removeAdoptionFileAdmin : removeAdoptionFilePartner;
    const res = await fn(id, file.id);
    setDeal((d) => ({ ...d, adoptionFiles: (res && res.adoptionFiles) || d.adoptionFiles }));
  }
  function handleAdoptionDownload(file) {
    const fn = isAdmin ? downloadAdoptionFileAdmin : downloadAdoptionFilePartner;
    return fn(id, file.id, file.originalName);
  }

  // 다이얼로그 OK — PUT + 낙관적 업데이트 + 실패 시 롤백 / 성공 시 재조회 ('도입' 은 confirmAdoption 으로 분기)
  async function confirmStageChange() {
    const nextStatus = stageDialogTarget;
    if (!deal || !nextStatus) {
      setStageDialogOpen(false);
      return;
    }
    if (nextStatus === "adoption") return confirmAdoption();
    const memo = stageDialogMemo.trim();
    const prevStatus = deal.status;
    const prevMemo = deal.partnerMemo;
    setStageDialogOpen(false);
    setDeal((d) => ({ ...d, status: nextStatus, partnerMemo: memo ? memo : d.partnerMemo }));
    try {
      const fn = isAdmin ? updateDealAdmin : updateDealPartner;
      const payload = { status: nextStatus };
      // partner 만 partner_memo 작성 가능 — admin 은 status 만 변경
      if (isPartner && memo) payload.partnerMemo = memo;
      await fn(id, payload);
      message.success(t("partners.deals.detail.statusChanged"));
    } catch (err) {
      setDeal((d) => ({ ...d, status: prevStatus, partnerMemo: prevMemo }));
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("partners.deals.detail.statusChangeFailed");
      message.error(msg);
      return;
    }
    // 최종 수정일(updatedAt) 동기화 — 재조회 실패 시 낙관적 반영 유지(변경은 성공)
    try {
      await reloadDeal();
    } catch (e) {
      // 무시
    }
  }

  // 담당자 변경 성공 — 팝업을 닫고 딜·이력을 다시 불러온다.
  // 관리자 등록 딜이 파트너사로 넘어가면 isAdminCreated 가 false 가 되어 영업단계·수정 버튼이 사라진다.
  async function handleOwnerChanged() {
    setOwnerModalOpen(false);
    setHistoryVersion((v) => v + 1);
    try {
      const d = await getDealAdmin(id);
      setDeal(d);
      setAdminMemo(d.adminMemo || "");
    } catch (e) {
      // 변경은 성공 — 재조회 실패 시 새로고침으로 확인
    }
  }

  // 관리자 메모 저장 — admin only
  async function handleMemoSave() {
    if (memoSaving) return;
    setMemoSaving(true);
    try {
      await updateDealAdmin(id, { adminMemo });
      message.success(t("partners.deals.detail.memoSaved"));
      // 저장 후 서버 최신 상태 1회 재조회 — 저장 중 다른 관리자가 바꾼 상태/메모까지 반영.
      // 재조회 실패 시엔 로컬 반영으로 폴백(저장 자체는 성공했으므로 에러 미노출)
      try {
        const d = await getDealAdmin(id);
        setDeal(d);
        setAdminMemo(d.adminMemo || "");
      } catch (e) {
        setDeal((d) => ({ ...d, adminMemo }));
      }
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("partners.deals.detail.memoSaveFailed");
      message.error(msg);
    } finally {
      setMemoSaving(false);
    }
  }

  async function handleQuotationPick(file) {
    if (!file) return;
    setQuotationUploading(true);
    try {
      const res = await uploadQuotation(id, file);
      setDeal((d) => ({
        ...d,
        hasQuotation: true,
        quotationOriginalName: (res && res.quotationOriginalName) || file.name,
        quotationSizeBytes: (res && res.quotationSizeBytes) || file.size,
      }));
      message.success(t("partners.deals.detail.quotationUploaded"));
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        (err && err.message) ||
        t("partners.deals.detail.quotationUploadFailed");
      message.error(msg);
    } finally {
      setQuotationUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleQuotationDownload() {
    if (!deal || !deal.hasQuotation) return;
    try {
      const fn = isAdmin ? downloadQuotationAdmin : downloadQuotationPartner;
      await fn(id, deal.quotationOriginalName);
    } catch (err) {
      message.error(err.message || t("partners.deals.detail.quotationDownloadFailed"));
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-50">
        <Navbar />
        <main className="flex-1 py-16">
          <Container>
            <p className="text-center text-sm text-slate-400">{t("partners.common.loading")}</p>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !deal) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-50">
        <Navbar />
        <main className="flex-1 py-16">
          <Container>
            <p className="text-center text-sm text-slate-500">
              {t("partners.deals.detail.notFound")}
            </p>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }

  // 수정 권한: 파트너는 본인 계정이 등록한 딜(서버 FORBIDDEN_NOT_OWNER 가드와 일치),
  // 관리자는 본인 등록 딜
  const canEdit = (isOwnPartnerDeal || isOwnAdminDeal) && !isDeletedDeal;
  // 삭제 권한: 수정과 별개 — 서버 DELETE 는 관리자에게 전체 딜 허용(#272)이므로 기존 유지.
  // 파트너는 등록한 계정만(등록자가 정지·삭제된 딜은 관리자가 처리)
  const canDelete = (isOwnPartnerDeal || isAdmin) && !isDeletedDeal;
  // 같은 회사 다른 계정이 담당하는 딜 — 열람 전용임을 알린다(버튼이 왜 없는지)
  const readOnlyNotOwner = isPartner && !isOwnPartnerDeal && !isDeletedDeal;
  // 담당자 변경 — 관리자, 삭제·도입 완료 딜 제외(서버도 404/400 으로 막는다)
  const canChangeOwner = isAdmin && !isDeletedDeal && deal.status !== "adoption";
  // 헤더 등록자/담당 표시 — 이관된 딜만 담당을 따로 보인다.
  //   관리자: 등록자(관리자 등록 딜이면 등록 관리자) + 담당 계정(파트너사)
  //   파트너: 담당 계정만(서버가 이관된 딜의 타사 등록자명을 내리지 않는다)
  const registrantName = deal.createdByName || deal.createdByAdminName || "-";
  const ownerLabel = deal.isTransferred
    ? `${deal.ownerName || "-"}${isAdmin && deal.partnerCompanyName ? ` (${deal.partnerCompanyName})` : ""}`
    : null;
  // '영업 진행 사항' 제목 옆 최종 수정 일시·경과일 (딜 전체 마지막 수정 updated_at 기준)
  const lastUpdatedAt = formatLocalDateTime(deal.updatedAt);
  const lastUpdatedDays = elapsedDays(deal.updatedAt);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-4xl">
            <header className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <DealStageBadge deal={deal} />
                  {(isAdmin || !ownerLabel) && (
                    <span className="text-xs text-slate-500">
                      {t("partners.deals.detail.registeredBy")}: {registrantName}
                    </span>
                  )}
                  {ownerLabel && (
                    <span className="text-xs text-slate-500">
                      {t("partners.deals.detail.ownedBy")}: {ownerLabel}
                    </span>
                  )}
                  <span className="text-xs text-slate-500">
                    {t("partners.deals.detail.registeredAt")}: {formatLocalDate(deal.createdAt)}
                  </span>
                </div>
                <h1 className="mt-3 text-2xl font-bold text-slate-900 md:text-3xl">
                  {deal.projectName}
                </h1>
              </div>
              <div className="flex flex-wrap gap-2">
                {editMode ? (
                  <>
                    <button
                      type="button"
                      className="btw-btn-ghost"
                      disabled={saving}
                      onClick={cancelEdit}
                    >
                      {t("partners.deals.detail.cancel")}
                    </button>
                    <Button variant="accent" onClick={saveEdit} disabled={saving}>
                      {saving
                        ? t("partners.deals.form.submitting")
                        : t("partners.deals.detail.save")}
                    </Button>
                  </>
                ) : (
                  <>
                    {canEdit && (
                      <button type="button" className="btw-btn-ghost" onClick={enterEdit}>
                        {t("partners.deals.detail.edit")}
                      </button>
                    )}
                    {canChangeOwner && (
                      <button
                        type="button"
                        className="btw-btn-ghost"
                        onClick={() => setOwnerModalOpen(true)}
                      >
                        {t("admin.deals.ownerChange.button")}
                      </button>
                    )}
                    <button type="button" className="btw-btn-ghost" onClick={backToList}>
                      ← {t("partners.deals.detail.backToList")}
                    </button>
                  </>
                )}
              </div>
            </header>

            {readOnlyNotOwner && (
              <p className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                {t("partners.deals.detail.readOnlyNotOwner", {
                  name: deal.ownerName || deal.createdByName || "-",
                })}
              </p>
            )}

            {/* 상태 배너 — **header 밖**에 둔다. header 는 flex justify-between 으로 제목 열과
                버튼 열이 폭을 나눠 쓰므로, 안에 두면 배너가 제목 열 폭에 갇혀 좁게 찌그러진다.
                여기로 빼면 본문과 같은 전체 폭을 쓴다.
                세 배너는 서로 배타적이라 항상 하나만 뜬다(아래 조건 참고). */}
            {/* ① 심사 대기 — 승인 전에는 딜 목록·통계·실적에 들어가지 않는다는 점을 알린다 */}
            {reviewPending && !isDeletedDeal && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
                <p className="font-semibold text-amber-800">
                  {t("partners.deals.detail.reviewPendingBanner")}
                </p>
                <p className="mt-1 text-xs text-amber-700">
                  {t("partners.deals.detail.reviewPendingHint")}
                </p>
              </div>
            )}
            {/* ② 반려 — 반려 딜은 삭제된 딜이기도 하므로 아래 '삭제된 딜' 배너보다 먼저 걸러 내
                이 배너만 보여준다. 둘 다 뜨면 파트너가 무엇이 원인인지 모른다.
                사유 없이 반려는 서버가 막으므로(400) 사유는 항상 있다. */}
            {reviewRejected && (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm">
                <p className="font-semibold text-rose-700">
                  {t("partners.deals.detail.reviewRejectedBanner")}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-xs text-rose-700">
                  {t("partners.deals.detail.reviewRejectedReason")}:{" "}
                  {deal.reviewRejectReason || "-"}
                </p>
                <p className="mt-2 text-xs text-slate-600">
                  {t("partners.deals.detail.reviewRejectedHint")}
                </p>
                {/* 되살리기가 없으므로 이 딜에서 할 수 있는 일은 '새로 등록' 뿐이다 —
                    막다른 화면에 출구를 둔다. 관리자에게는 보여줄 이유가 없다. */}
                {isPartner && (
                  <div className="mt-3">
                    <Button size="sm" onClick={() => navigate("/partners/deals/new")}>
                      {t("partners.deals.detail.registerAgain")}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {/* ③ 삭제된 딜 — 반려 외의 삭제(파트너·관리자 직접 삭제, 파트너사 삭제 cascade) */}
            {isDeletedDeal && !reviewRejected && (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm">
                <p className="font-semibold text-rose-700">
                  {t("partners.deals.detail.deletedBanner")}
                </p>
                <p className="mt-1 text-xs text-rose-600">
                  {t("partners.deals.detail.deletedMeta", {
                    at: deal.deletedAt ? String(deal.deletedAt).slice(0, 10) : "-",
                    by: deal.deletedByName || "-",
                  })}
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  {t("partners.deals.detail.deletedReasonLabel")}:{" "}
                  {deal.deletedReason || t("partners.deals.detail.deletedNoReason")}
                </p>
              </div>
            )}

            <div className="mt-6 grid gap-6">
              {/* 영업단계 변경 — 파트너·본인 등록 딜 관리자에게만 노출 (서버 수정 가드와 일치). */}
              {canChangeStatus && (
                <DetailSection title={t("partners.deals.detail.adminStatus")}>
                  <div className="grid grid-cols-7 gap-2 md:col-span-2">
                    {SALES_STAGES.map((s) => {
                      const isCurrent = deal.status === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => handleStatusChange(s)}
                          className={`truncate rounded-lg border px-2 py-2 text-xs font-medium transition sm:text-sm ${
                            isCurrent
                              ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                              : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                          }`}
                        >
                          {t(`partners.deals.status.${s}`)}
                        </button>
                      );
                    })}
                  </div>
                </DetailSection>
              )}

              {editMode && (
                <DetailSection title={t("partners.deals.form.project")}>
                  <Input
                    label={t("partners.deals.form.projectName")}
                    required
                    value={form.projectName || ""}
                    onChange={(e) => setField("projectName", e.target.value)}
                    maxLength={200}
                  />
                </DetailSection>
              )}

              <DetailSection title={t("partners.deals.form.sectionCustomer")}>
                {editMode ? (
                  <>
                    <CompanySearchSelect
                      label={t("partners.deals.form.customerCompany")}
                      required
                      alertOnTaken={isPartner}
                      value={form.customerCompany || ""}
                      onChange={(name) => setField("customerCompany", name)}
                      businessNumber={form.customerBusinessNumber || ""}
                      onBusinessNumberChange={(v) => setField("customerBusinessNumber", v)}
                      excludeDealId={id}
                    />
                    <Input
                      label={t("partners.deals.form.customerContactName")}
                      required
                      value={form.customerContactName || ""}
                      onChange={(e) => setField("customerContactName", e.target.value)}
                      maxLength={50}
                    />
                    <Input
                      label={t("partners.deals.form.customerContactPosition")}
                      value={form.customerContactPosition || ""}
                      onChange={(e) => setField("customerContactPosition", e.target.value)}
                      maxLength={100}
                    />
                    <Input
                      label={t("partners.deals.form.customerPhone")}
                      value={form.customerPhone || ""}
                      onChange={(e) => setField("customerPhone", formatPhone(e.target.value))}
                      maxLength={13}
                    />
                    <Input
                      label={t("partners.deals.form.customerEmail")}
                      type="email"
                      value={form.customerEmail || ""}
                      onChange={(e) => setField("customerEmail", e.target.value)}
                      maxLength={120}
                    />
                  </>
                ) : (
                  <>
                    <Row
                      label={t("partners.deals.form.customerCompany")}
                      value={deal.customerCompany}
                    />
                    <Row
                      label={t("partners.deals.form.customerBusinessNumber")}
                      value={
                        deal.customerBusinessNumber
                          ? isBusinessNumberComplete(deal.customerBusinessNumber)
                            ? formatCustomerBusinessNumber(deal.customerBusinessNumber)
                            : // 뒷자리가 안 채워진 값 — 조회 앞자리뿐이라는 점을 밝혀 둔다
                              `${formatCustomerBusinessNumber(deal.customerBusinessNumber)} (${t(
                                "partners.deals.form.customerBusinessNumberRef",
                              )})`
                          : "-"
                      }
                    />
                    <Row
                      label={t("partners.deals.form.customerContactName")}
                      value={deal.customerContactName}
                    />
                    <Row
                      label={t("partners.deals.form.customerContactPosition")}
                      value={deal.customerContactPosition || "-"}
                    />
                    <Row
                      label={t("partners.deals.form.customerPhone")}
                      value={deal.customerPhone || "-"}
                    />
                    <Row
                      label={t("partners.deals.form.customerEmail")}
                      value={deal.customerEmail || "-"}
                    />
                  </>
                )}
              </DetailSection>

              <DetailSection title={t("partners.deals.form.sectionPartner")}>
                {editMode && isPartner ? (
                  <>
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                      <label className="text-xs font-medium text-slate-700">
                        {t("partners.deals.form.salesRepName")}{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <select
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-accent focus:ring-2 focus:ring-accent/30"
                        value={form.partnerSalesRepId || ""}
                        onChange={(e) => setField("partnerSalesRepId", e.target.value)}
                      >
                        <option value="">{t("partners.deals.form.salesRepSelect")}</option>
                        {/* 기존 선택값이 옵션에 없으면(삭제됨) — 재선택 안내용 자리 표시 */}
                        {form.partnerSalesRepId && !selectedRep && (
                          <option value={form.partnerSalesRepId} disabled>
                            {t("partners.deals.form.salesRepDeleted")}
                          </option>
                        )}
                        {salesReps.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                            {r.phone ? ` · ${r.phone}` : ""}
                          </option>
                        ))}
                      </select>
                      {salesReps.length === 0 ? (
                        <p className="text-xs text-rose-600">
                          {t("partners.deals.form.salesRepEmptyHint")}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500">
                          {t("partners.deals.form.salesRepHint")}
                        </p>
                      )}
                    </div>
                    <Input
                      label={t("partners.deals.form.salesRepPhone")}
                      value={selectedRep?.phone || ""}
                      disabled
                      tabIndex={-1}
                      inputClassName="bg-slate-100 cursor-not-allowed"
                    />
                    <Input
                      label={t("partners.deals.form.salesRepEmail")}
                      value={selectedRep?.email || ""}
                      disabled
                      tabIndex={-1}
                      inputClassName="bg-slate-100 cursor-not-allowed"
                    />
                  </>
                ) : (
                  <>
                    <Row label={t("partners.deals.form.salesRepName")} value={deal.salesRepName} />
                    <Row
                      label={t("partners.deals.form.salesRepPhone")}
                      value={deal.salesRepPhone || "-"}
                    />
                    <Row
                      label={t("partners.deals.form.salesRepEmail")}
                      value={deal.salesRepEmail || "-"}
                    />
                  </>
                )}
              </DetailSection>

              <DetailSection title={t("partners.deals.form.sectionDeal")}>
                {editMode ? (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-slate-700">
                        {t("partners.deals.form.poDate")}
                      </label>
                      <DatePicker
                        className="!h-[38px]"
                        format="YYYY-MM-DD"
                        locale={locale}
                        value={form.poExpectedDate ? dayjs(form.poExpectedDate) : null}
                        onChange={(d) =>
                          setField("poExpectedDate", d ? d.format("YYYY-MM-DD") : "")
                        }
                        placeholder={t("partners.deals.form.poDatePlaceholder")}
                      />
                    </div>
                    <Input
                      label={t("partners.deals.form.poQuarter")}
                      value={formatQuarter(quarterFromDate(form.poExpectedDate)) || "-"}
                      disabled
                      tabIndex={-1}
                      inputClassName="bg-slate-100 cursor-not-allowed"
                      hint={t("partners.deals.form.quarterAuto")}
                    />
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                      <label className="text-xs font-medium text-slate-700">
                        {t("partners.deals.form.poAmount")}
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-60 rounded-lg border border-slate-300 px-3 py-2 text-right text-sm tabular-nums focus:border-accent focus:ring-2 focus:ring-accent/30"
                          value={formatAmount(form.poAmount)}
                          onChange={(e) => setField("poAmount", parseAmount(e.target.value))}
                          placeholder={t("partners.deals.form.poAmountPlaceholder")}
                        />
                        <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/40"
                            checked={!!form.vatExcluded}
                            onChange={(e) => setField("vatExcluded", e.target.checked)}
                          />
                          {t("partners.deals.form.vatExcluded")}
                        </label>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Textarea
                        label={t("partners.deals.form.productDetail")}
                        rows={3}
                        maxLength={500}
                        value={form.productDetail || ""}
                        onChange={(e) => setField("productDetail", e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Textarea
                        label={t("partners.deals.form.salesProgress")}
                        rows={6}
                        maxLength={2000}
                        value={form.salesProgress || ""}
                        onChange={(e) => setField("salesProgress", e.target.value)}
                      />
                    </div>
                    <Input
                      label={t("partners.deals.form.channel")}
                      value={form.channel || ""}
                      onChange={(e) => setField("channel", e.target.value)}
                      maxLength={100}
                    />
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-slate-700">
                        {t("partners.deals.form.poc")}
                      </label>
                      <div className="flex gap-2">
                        {[
                          ["required", t("partners.deals.form.pocRequired")],
                          ["not_required", t("partners.deals.form.pocNotRequired")],
                          ["undecided", t("partners.deals.form.pocUndecided")],
                        ].map(([val, label]) => (
                          <label
                            key={val}
                            className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                              form.pocRequired === val
                                ? "border-accent bg-accent/10 text-accent"
                                : "border-slate-300 bg-white text-slate-700"
                            }`}
                          >
                            <input
                              type="radio"
                              name="pocRequired"
                              value={val}
                              className="hidden"
                              checked={form.pocRequired === val}
                              onChange={() => setField("pocRequired", val)}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-1.5 text-sm text-slate-700 md:col-span-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/40"
                        checked={!!form.brochureDelivered}
                        onChange={(e) => setField("brochureDelivered", e.target.checked)}
                      />
                      {t("partners.deals.form.brochure")}
                    </label>
                  </>
                ) : (
                  <>
                    <Row
                      label={t("partners.deals.form.poDate")}
                      value={deal.poExpectedDate ? String(deal.poExpectedDate).slice(0, 10) : "-"}
                    />
                    <Row
                      label={t("partners.deals.form.poQuarter")}
                      value={formatQuarter(deal.poExpectedQuarter) || "-"}
                    />
                    <Row
                      label={t("partners.deals.form.productDetail")}
                      value={deal.productDetail || "-"}
                    />
                    <Row
                      label={t("partners.deals.form.poAmount")}
                      value={`${formatKRW(deal.poAmount)} ${t("common.unitKrw")}${deal.vatExcluded ? " " + t("partners.deals.detail.vatNote") : ""}`}
                    />
                    <Row label={t("partners.deals.form.channel")} value={deal.channel || "-"} />
                    <Row
                      label={t("partners.deals.form.poc")}
                      value={t(`partners.deals.form.poc${cap(deal.pocRequired)}`)}
                    />
                    <Row
                      label={t("partners.deals.form.brochure")}
                      value={deal.brochureDelivered ? "✓" : "-"}
                    />
                  </>
                )}
                <Row
                  label={t("partners.deals.detail.quotation")}
                  value={
                    <span className="inline-flex flex-wrap items-center justify-end gap-2">
                      {/* 삭제된 딜(반려 포함)은 서버가 첨부 조회도 is_deleted=0 으로 막는다
                          (partner·admin dealFileRoutes 양쪽) → 누르면 404 다. 파일명은 무슨
                          파일을 냈는지 알 수 있게 남기고 링크만 뗀다. */}
                      {deal.hasQuotation ? (
                        isDeletedDeal ? (
                          <span className="text-slate-500">{deal.quotationOriginalName}</span>
                        ) : (
                          <button
                            type="button"
                            onClick={handleQuotationDownload}
                            className="text-accent underline-offset-2 hover:underline"
                          >
                            {deal.quotationOriginalName}
                          </button>
                        )
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                      {canUploadQuotation && (
                        <>
                          <button
                            type="button"
                            disabled={quotationUploading}
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-accent hover:text-accent disabled:opacity-60"
                          >
                            {quotationUploading
                              ? t("partners.deals.detail.quotationUploading")
                              : deal.hasQuotation
                                ? t("partners.deals.detail.quotationReplace")
                                : t("partners.deals.detail.quotationUpload")}
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.xlsx,.xls,.doc,.docx,.hwp,.hwpx"
                            className="hidden"
                            onChange={(e) =>
                              handleQuotationPick(e.target.files && e.target.files[0])
                            }
                          />
                        </>
                      )}
                    </span>
                  }
                />
                {/* 영업 진행 사항은 내용이 없어도 제목·최종 수정 일시를 보여준다(빈 칸으로 표시) */}
                {!editMode && (
                  <div className="mt-3 md:col-span-2">
                    {/* 제목 옆 최종 수정 일시 — 딜 전체 마지막 수정(updated_at) 기준, 관리 대상 딜 정체 일수와 같은 값 */}
                    <div className="mb-2 flex flex-wrap items-baseline gap-x-2 text-xs font-medium text-slate-500">
                      <span>{t("partners.deals.form.salesProgress")}</span>
                      {lastUpdatedAt && (
                        <span className="font-normal text-slate-400">
                          {t("partners.deals.detail.lastUpdated", {
                            date: lastUpdatedAt,
                            count: lastUpdatedDays,
                          })}
                        </span>
                      )}
                    </div>
                    <div className="min-h-[4rem] rounded-lg bg-slate-50 p-5 text-base leading-relaxed text-slate-800 whitespace-pre-wrap">
                      {deal.salesProgress}
                    </div>
                  </div>
                )}
                {!editMode && deal.partnerMemo && (
                  <div className="mt-3 md:col-span-2">
                    <div className="mb-2 text-xs font-medium text-slate-500">
                      {t("partners.deals.detail.partnerMemo")}
                    </div>
                    <div className="rounded-lg bg-slate-50 p-5 text-base leading-relaxed text-slate-800 whitespace-pre-wrap">
                      {deal.partnerMemo}
                    </div>
                  </div>
                )}
              </DetailSection>

              {/* 도입 증빙 파일 — 도입 상태이거나 파일이 남아 있으면 표시. 교체는 도입 처리 본인만. */}
              {showAdoptionSection && (
                <AdoptionFilesSection
                  files={adoptionFiles}
                  canManage={canManageAdoptionFiles}
                  isAdoptionStage={deal.status === "adoption"}
                  onDownload={handleAdoptionDownload}
                  onAdd={handleAdoptionAdd}
                  onRemove={handleAdoptionRemove}
                  limits={deal.adoptionFileLimits}
                />
              )}

              {/* 관리자 → 파트너 메시지(공개 스레드) — 파트너 소유 딜만.
                  관리자 직접 등록 딜(partnerId null)은 볼 파트너가 없어 섹션 자체를 숨김.
                  작성은 관리자 + 삭제되지 않은 딜(서버 400 가드와 일치). 수정 모드에서는 숨김. */}
              {!editMode && (isPartner || (isAdmin && !!deal.partnerId)) && (
                <DealMessagesSection
                  dealId={id}
                  isAdmin={isAdmin}
                  canWrite={isAdmin && !isDeletedDeal && !!deal.partnerId}
                  recipientEmail={deal.salesRepEmail}
                />
              )}

              {/* 관리자 내부 메모 — admin only.
                  DetailSection 의 2열 그리드(md:grid-cols-2)를 쓰면 textarea·버튼이 좌우 셀로
                  쪼개지므로, 단독 섹션으로 구성. 내부 전용임을 앰버 톤으로 시각 구분. */}
              {isAdmin && !isDeletedDeal && (
                <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 md:p-7">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-1">
                    <h2 className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                      {t("partners.deals.detail.adminMemo")}
                    </h2>
                    <span className="text-[11px] font-medium text-amber-600/90">
                      {t("partners.deals.detail.adminMemoHint")}
                    </span>
                  </div>
                  <div className="mt-3">
                    <Textarea
                      rows={5}
                      maxLength={1000}
                      value={adminMemo}
                      placeholder={t("partners.deals.detail.adminMemoPlaceholder")}
                      onChange={(e) => setAdminMemo(e.target.value)}
                    />
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Button
                      variant="accent"
                      size="sm"
                      onClick={handleMemoSave}
                      disabled={adminMemo === (deal.adminMemo || "")}
                      loading={memoSaving}
                    >
                      {memoSaving
                        ? t("partners.deals.detail.memoSaving")
                        : t("partners.deals.detail.save")}
                    </Button>
                  </div>
                </section>
              )}

              {/* 변경 이력 — admin only. 파트너 PUT 만 기록(현재 정책) */}
              {isAdmin && (
                <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("admin.deals.changeHistory.title")}
                  </h2>
                  <div className="mt-3">
                    <DealChangeHistory key={historyVersion} dealId={id} />
                  </div>
                </section>
              )}

              {/* 위험 영역 — 페이지 최하단. 삭제는 비가역이라 시각적으로 격리. read 모드만 노출. */}
              {canDelete && !editMode && (
                <section className="mt-2 rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-rose-700">
                    {t("partners.deals.detail.dangerZone")}
                  </h2>
                  <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="text-sm text-slate-700">
                      <p className="font-medium text-slate-900">
                        {t("partners.deals.detail.deleteSectionTitle")}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {t("partners.deals.detail.deleteSectionHint")}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/40 disabled:opacity-60"
                      disabled={deleting}
                      onClick={handleDelete}
                    >
                      {t("partners.deals.detail.delete")}
                    </button>
                  </div>
                </section>
              )}
            </div>
          </div>
        </Container>
      </main>
      <Footer />

      {/* 영업단계 변경 다이얼로그 — partner 는 메모 입력 가능, admin 은 단순 confirm */}
      <Modal
        open={stageDialogOpen}
        title={t("partners.deals.detail.statusChangeConfirmTitle")}
        okText={t("partners.deals.detail.statusChangeConfirmOk")}
        cancelText={t("partners.deals.detail.statusChangeConfirmCancel")}
        onOk={confirmStageChange}
        onCancel={() => !stageSubmitting && setStageDialogOpen(false)}
        okButtonProps={{ disabled: adoptionOkDisabled, loading: stageSubmitting }}
        cancelButtonProps={{ disabled: stageSubmitting }}
        closable={!stageSubmitting}
        maskClosable={!stageSubmitting}
        destroyOnClose
      >
        {deal && stageDialogTarget && (
          <>
            <p className="text-sm text-slate-700">
              {t("partners.deals.detail.statusChangeConfirmContent", {
                from: t(`partners.deals.status.${deal.status}`),
                to: t(`partners.deals.status.${stageDialogTarget}`),
              })}
            </p>
            {isAdoptionTarget && (
              <div className="mt-4">
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  {t("partners.deals.detail.adoptionFilesLabel")}
                </label>
                <p className="mb-2 text-xs text-slate-500">
                  {adoptionFiles.length > 0
                    ? t("partners.deals.detail.adoptionExistingHint", {
                        count: adoptionFiles.length,
                      })
                    : t("partners.deals.detail.adoptionRequiredHint")}
                </p>
                <AdoptionFilePicker
                  files={stageFiles}
                  onChange={setStageFiles}
                  onError={(m) => message.error(m)}
                  existingCount={adoptionFiles.length}
                  limits={deal.adoptionFileLimits}
                  disabled={stageSubmitting}
                />
              </div>
            )}
            {isPartner && (
              <div className="mt-4">
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  {t("partners.deals.detail.partnerMemoLabel")}
                </label>
                <Textarea
                  rows={4}
                  maxLength={1000}
                  placeholder={t("partners.deals.detail.partnerMemoPlaceholder")}
                  value={stageDialogMemo}
                  onChange={(e) => setStageDialogMemo(e.target.value)}
                />
              </div>
            )}
          </>
        )}
      </Modal>
      {isAdmin && (
        <DealOwnerChangeModal
          open={ownerModalOpen}
          deal={deal}
          onClose={() => setOwnerModalOpen(false)}
          onChanged={handleOwnerChanged}
        />
      )}
    </div>
  );
}

function cap(s) {
  if (!s) return "Undecided";
  if (s === "required") return "Required";
  if (s === "not_required") return "NotRequired";
  return "Undecided";
}

function DetailSection({ title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
      <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </h2>
      <div className="mt-3 grid gap-x-6 gap-y-4 md:grid-cols-2">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0 md:border-b-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="max-w-[60%] text-right text-sm text-slate-800">{value}</span>
    </div>
  );
}
